import { matchRoutes, type RouteObject } from "react-router-dom";
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { IS_PROD } from "@bridgesplit/utils";
import {
    BORROW_SLUG,
    DISCORD_SLUG,
    EARN_SLUG,
    ECOSYSTEM_SLUG,
    LEND_SLUG,
    LEND_VAULT_DETAIL_SLUG,
    LOAN_SLUG,
    LOCKBOX_DETAIL_SLUG,
    LOCKBOX_SLUG,
    LOOP_DETAIL_SLUG,
    LOOPS_SLUG,
    MARKET_SLUG,
    POINTS_SLUG,
    PORTFOLIO_OVERVIEW_SLUG,
    SIGNUP_SLUG,
    STRATEGY_LOANS_SLUG,
    TWITTER_SLUG,
    VAULT_MANAGER_SLUG
} from "app/constants";

export const RUM_ENDPOINT = "/rum";

const UNKNOWN_ROUTE = "unknown";

const ROUTE_PATTERNS: RouteObject[] = [
    { path: "/" },
    { path: LEND_SLUG },
    { path: EARN_SLUG },
    { path: BORROW_SLUG },
    { path: LOOPS_SLUG },
    { path: SIGNUP_SLUG },
    { path: `${MARKET_SLUG}/:symbol` },
    { path: `${LEND_VAULT_DETAIL_SLUG}/:vaultIdentifier` },
    { path: `${EARN_SLUG}/:vaultIdentifier` },
    { path: `${LOOP_DETAIL_SLUG}/:address` },
    { path: PORTFOLIO_OVERVIEW_SLUG },
    { path: `${LOAN_SLUG}/:address` },
    { path: `${STRATEGY_LOANS_SLUG}/:address` },
    { path: `${VAULT_MANAGER_SLUG}/:vaultIdentifier` },
    { path: POINTS_SLUG },
    { path: TWITTER_SLUG },
    { path: DISCORD_SLUG },
    { path: ECOSYSTEM_SLUG },
    { path: LOCKBOX_SLUG },
    { path: `${LOCKBOX_DETAIL_SLUG}/:address` }
];

export function resolveRoutePattern(pathname: string): string {
    const matches = matchRoutes(ROUTE_PATTERNS, pathname);
    return matches?.[matches.length - 1]?.route.path ?? UNKNOWN_ROUTE;
}

interface VitalsEvent {
    metric: Metric["name"];
    value: number;
    rating: Metric["rating"];
    id: string;
    navigationType: Metric["navigationType"];
    delta: number;
    entryRoute: string;
    pathname: string;
    appVersion: string | undefined;
    buildTimestamp: string | undefined;
    sessionId: string;
    effectiveType: string | undefined;
    deviceMemory: number | undefined;
    viewportWidth: number;
}

const buffer: Metric[] = [];
const sent = new Set<string>();

function getSessionId(): string {
    const fromSentry = (window as { __DEPLOYMENT_INFO__?: { sessionId?: string } }).__DEPLOYMENT_INFO__?.sessionId;
    if (fromSentry) return fromSentry;
    // crypto.randomUUID is undefined in older/non-secure contexts (Safari <15.4);
    // a throw here would abort all vitals collection via the outer try/catch.
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function toEvent(metric: Metric, entryRoute: string, sessionId: string): VitalsEvent {
    const connection = (navigator as { connection?: { effectiveType?: string } }).connection;
    return {
        metric: metric.name,
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
        navigationType: metric.navigationType,
        delta: metric.delta,
        entryRoute,
        pathname: window.location.pathname,
        appVersion: process.env.NX_DEPLOY_VERSION,
        buildTimestamp: process.env.NX_DEPLOY_TIMESTAMP,
        sessionId,
        effectiveType: connection?.effectiveType,
        deviceMemory: (navigator as { deviceMemory?: number }).deviceMemory,
        viewportWidth: window.innerWidth
    };
}

function sendVitals(events: VitalsEvent[]): void {
    try {
        if (!events.length || typeof navigator.sendBeacon !== "function") return;
        navigator.sendBeacon(RUM_ENDPOINT, new Blob([JSON.stringify(events)], { type: "application/json" }));
    } catch {
        // RUM is best-effort; never surface transport failures to the user.
    }
}

export function initWebVitals(): void {
    if (!IS_PROD) return;

    try {
        const entryRoute = resolveRoutePattern(window.location.pathname);
        const sessionId = getSessionId();

        const record = (metric: Metric) => {
            buffer.push(metric);
        };

        onLCP(record);
        onINP(record);
        onCLS(record);
        onFCP(record);
        onTTFB(record);

        // Registered after the web-vitals handlers so the buffer is populated
        // before a flush reads it. Dedupe by id (keeping the latest entry) then
        // skip ids already sent, so a metric goes out at most once per pageload.
        const flush = () => {
            try {
                const latest = new Map<string, Metric>();
                for (const metric of buffer) latest.set(metric.id, metric);

                const events: VitalsEvent[] = [];
                for (const metric of latest.values()) {
                    if (sent.has(metric.id)) continue;
                    sent.add(metric.id);
                    events.push(toEvent(metric, entryRoute, sessionId));
                }
                sendVitals(events);
            } catch {
                // swallow
            }
        };

        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") flush();
        });
        window.addEventListener("pagehide", flush);
    } catch {
        // swallow
    }
}
